import React, { useState, useEffect, useCallback, useRef } from 'react';
import { billsAPI } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CYCLE_LABELS = {
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly'
};

function getDaysUntilDue(dueDateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Strip any time component before parsing so we compare dates only
  const datePart = dueDateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const due = new Date(year, month - 1, day);
  return Math.round((due - today) / 86400000);
}

function getStatusGroup(bill) {
  if (!bill.is_recurring && bill.is_paid) return 'paid';
  const days = getDaysUntilDue(bill.due_date);
  if (days < 0) return 'overdue';
  if (days <= 7) return 'dueSoon';
  return 'upcoming';
}

const GROUP_CONFIG = {
  overdue: { label: 'Overdue', color: 'var(--accent-coral)', badgeClass: 'badge-danger' },
  dueSoon: { label: 'Due Soon', color: 'var(--accent-gold, #f5a623)', badgeClass: 'badge-warning' },
  upcoming: { label: 'Upcoming', color: 'var(--text-secondary)', badgeClass: 'badge-primary' },
  paid: { label: 'Paid (One-Time)', color: 'var(--accent-green, #4caf50)', badgeClass: 'badge-success' }
};

function DaysChip({ dueDateStr, isPaid, isRecurring }) {
  if (!isRecurring && isPaid) return <span className="badge badge-success">Paid</span>;
  const days = getDaysUntilDue(dueDateStr);
  if (days < 0) return <span className="badge badge-danger">{Math.abs(days)}d overdue</span>;
  if (days === 0) return <span className="badge badge-warning">Due today</span>;
  if (days === 1) return <span className="badge badge-warning">Due tomorrow</span>;
  if (days <= 7) return <span className="badge badge-warning">Due in {days}d</span>;
  return <span className="badge badge-primary">In {days}d</span>;
}

export default function Bills() {
  const formRef = useRef(null);
  const [bills, setBills] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberTotals, setMemberTotals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [historyBill, setHistoryBill] = useState(null);
  const [historyPayments, setHistoryPayments] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [justPaid, setJustPaid] = useState(null); // bill id briefly highlighted after paying
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    name: '', amount: '', due_date: '', is_recurring: false,
    recurring_cycle: 'monthly', category: '', notes: '', assigned_to: '',
    is_auto_draft: false
  });

  const fetchBills = useCallback(async () => {
    try {
      const response = await billsAPI.getAll();
      setBills(response.data);
    } catch (error) {
      console.error('Error fetching bills:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/household`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  }, []);

  const fetchTotals = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/bills/totals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberTotals(response.data);
    } catch (error) {
      console.error('Error fetching totals:', error);
    }
  }, []);

  useEffect(() => { fetchBills(); fetchMembers(); fetchTotals(); }, [fetchBills, fetchMembers, fetchTotals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, assigned_to: formData.assigned_to || null };
      if (editingId) { await billsAPI.update(editingId, payload); }
      else { await billsAPI.create(payload); }
      fetchBills(); fetchTotals(); resetForm();
    } catch (error) {
      console.error('Error saving bill:', error);
      alert('Failed to save bill');
    }
  };

  const handleTogglePaid = async (bill) => {
    const markingPaid = !bill.is_paid;
    try {
      await billsAPI.togglePaid(bill.id, markingPaid);
      if (markingPaid && bill.is_recurring) {
        // Flash the row briefly to show the cycle advanced
        setJustPaid(bill.id);
        setTimeout(() => setJustPaid(null), 2000);
      }
      fetchBills(); fetchTotals();
    } catch (error) {
      console.error('Error toggling bill status:', error);
    }
  };

  const handleEdit = (bill) => {
    setFormData({
      name: bill.name,
      amount: bill.amount,
      due_date: bill.due_date.split('T')[0],
      is_recurring: bill.is_recurring,
      recurring_cycle: bill.recurring_cycle || 'monthly',
      category: bill.category || '',
      notes: bill.notes || '',
      assigned_to: bill.assigned_to || '',
      is_auto_draft: !!bill.is_auto_draft
    });
    setEditingId(bill.id);
    setShowForm(true);
    setTimeout(() => {
      if (formRef.current) {
        const top = formRef.current.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }, 50);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this bill?')) {
      try { await billsAPI.delete(id); fetchBills(); fetchTotals(); }
      catch (error) { console.error('Error deleting bill:', error); }
    }
  };

  const handleViewHistory = async (bill) => {
    setHistoryBill(bill);
    setHistoryLoading(true);
    try {
      const response = await billsAPI.getPayments(bill.id);
      setHistoryPayments(response.data);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setHistoryPayments([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', amount: '', due_date: '', is_recurring: false,
      recurring_cycle: 'monthly', category: '', notes: '', assigned_to: '', is_auto_draft: false });
    setEditingId(null);
    setShowForm(false);
  };

  const visibleBills = activeTab === 'all'
    ? bills
    : bills.filter(b => String(b.assigned_to) === activeTab);

  // Group bills by status
  const grouped = { overdue: [], dueSoon: [], upcoming: [], paid: [] };
  visibleBills.forEach(bill => grouped[getStatusGroup(bill)].push(bill));

  const unpaidTotal = visibleBills
    .filter(b => !(!b.is_recurring && b.is_paid))
    .reduce((sum, b) => sum + parseFloat(b.amount), 0);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Bills</h2>
          <p>Track and manage your household bills</p>
        </div>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); }} className="btn btn-primary">
          {showForm && !editingId ? 'Cancel' : '+ Add Bill'}
        </button>
      </div>

      {/* Person filter tabs */}
      {members.length > 0 && (
        <div style={{
          display: 'flex', gap: '8px', marginBottom: '16px',
          borderBottom: '1px solid var(--border-color)',
          overflowX: 'auto', flexWrap: 'nowrap'
        }}>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '8px 18px', fontSize: '14px', fontWeight: 600,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === 'all' ? '2px solid var(--accent-coral)' : '2px solid transparent',
              color: activeTab === 'all' ? 'var(--accent-coral)' : 'var(--text-muted)',
              whiteSpace: 'nowrap', transition: 'color 0.15s'
            }}
          >All</button>
          {members.map(member => (
            <button
              key={member.id}
              onClick={() => setActiveTab(String(member.id))}
              style={{
                padding: '8px 18px', fontSize: '14px', fontWeight: 600,
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: activeTab === String(member.id) ? '2px solid var(--accent-coral)' : '2px solid transparent',
                color: activeTab === String(member.id) ? 'var(--accent-coral)' : 'var(--text-muted)',
                whiteSpace: 'nowrap', transition: 'color 0.15s'
              }}
            >
              {member.full_name || member.username}
            </button>
          ))}
        </div>
      )}

      {/* Member totals */}
      {memberTotals.length > 0 && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bills by Member</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {memberTotals.map(member => (
              <div key={member.user_id} style={{
                flex: '1', minWidth: '140px', padding: '12px 16px',
                background: 'var(--bg-secondary)', borderRadius: '8px',
                borderLeft: '3px solid var(--accent-coral)'
              }}>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  {member.full_name || member.username}
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent-coral)' }}>
                  ${parseFloat(member.total_amount).toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {member.bill_count} bill{member.bill_count !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card card-table">
        {/* Add/Edit Form */}
        {showForm && (
          <div ref={formRef} className="form-section">
            <h3 style={{ marginBottom: '16px', fontSize: '15px' }}>{editingId ? 'Edit Bill' : 'Add New Bill'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Bill Name *</label>
                  <input type="text" className="form-input" value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})} required
                    placeholder="e.g. Electric, Water" />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount *</label>
                  <input type="number" step="0.01" className="form-input" value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})} required
                    placeholder="0.00" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Due Date *</label>
                  <input type="date" className="form-input" value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input type="text" className="form-input" placeholder="e.g. Utilities, Insurance"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Assign To</label>
                  <select className="form-select" value={formData.assigned_to}
                    onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}>
                    <option value="">Unassigned</option>
                    {members.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.full_name || member.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" className="checkbox" checked={formData.is_recurring}
                      onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})} />
                    Recurring Bill
                  </label>
                  {formData.is_recurring && (
                    <select className="form-select" value={formData.recurring_cycle} style={{ marginTop: '8px' }}
                      onChange={(e) => setFormData({...formData, recurring_cycle: e.target.value})}>
                      {Object.entries(CYCLE_LABELS).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Optional notes about this bill..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" className="checkbox" checked={formData.is_auto_draft}
                      onChange={(e) => setFormData({...formData, is_auto_draft: e.target.checked})} />
                    Auto-Drafted
                  </label>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '16px' }}>
                  <div className="flex gap-1">
                    <button type="submit" className="btn btn-success">{editingId ? 'Update' : 'Add'}</button>
                    <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="total-display">
          <span className="total-label">Unpaid Total:</span>
          <span className="total-amount danger">${unpaidTotal.toFixed(2)}</span>
        </div>

        {visibleBills.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📄</div>
            <p className="empty-state-text">{bills.length === 0 ? 'No bills yet' : 'No bills for this person'}</p>
            {bills.length === 0 && <button onClick={() => setShowForm(true)} className="btn btn-primary">Add Your First Bill</button>}
          </div>
        ) : (
          ['overdue', 'dueSoon', 'upcoming', 'paid'].map(group => {
            const groupBills = grouped[group];
            if (groupBills.length === 0) return null;
            const config = GROUP_CONFIG[group];
            return (
              <div key={group} style={{ marginBottom: '24px' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 0', marginBottom: '4px',
                  borderBottom: `2px solid ${config.color}`
                }}>
                  <span style={{ fontWeight: 700, fontSize: '13px', color: config.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {config.label}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {groupBills.length} bill{groupBills.length !== 1 ? 's' : ''}
                    {group !== 'paid' && (
                      <> — ${groupBills.reduce((s, b) => s + parseFloat(b.amount), 0).toFixed(2)}</>
                    )}
                  </span>
                </div>
                <div className="tracker-table-wrap">
                  <table className="tracker-table">
                    <thead>
                      <tr>
                        <th className="tracker-th col-check"></th>
                        <th className="tracker-th col-name">Name</th>
                        <th className="tracker-th col-amount">Amount</th>
                        <th className="tracker-th col-due">Due Date</th>
                        <th className="tracker-th col-category">Category</th>
                        <th className="tracker-th col-cycle">Cycle</th>
                        <th className="tracker-th col-assigned">Assigned To</th>
                        <th className="tracker-th col-status">Status</th>
                        <th className="tracker-th col-actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupBills.map(bill => (
                        <tr
                          key={bill.id}
                          className={`tracker-row${!bill.is_recurring && bill.is_paid ? ' tracker-row-paid' : ''}`}
                          style={justPaid === bill.id ? { background: 'var(--accent-green-faint, rgba(76,175,80,0.08))', transition: 'background 0.4s' } : {}}
                        >
                          <td className="tracker-td col-check">
                            <input
                              type="checkbox"
                              className="checkbox"
                              checked={!bill.is_recurring ? bill.is_paid : false}
                              title={bill.is_recurring ? `Mark as paid — next due date will advance automatically` : ''}
                              onChange={() => handleTogglePaid(bill)}
                            />
                          </td>
                          <td className="tracker-td col-name tracker-name">
                            <div>{bill.name}</div>
                            {!!bill.is_auto_draft && (
                              <span style={{
                                display: 'inline-block', marginTop: 3, fontSize: 10, fontWeight: 700,
                                padding: '1px 6px', borderRadius: 4,
                                background: 'var(--accent-teal-soft)', color: 'var(--accent-teal)',
                                border: '1px solid rgba(78,205,196,0.25)', textTransform: 'uppercase', letterSpacing: '0.06em'
                              }}>Auto Draft</span>
                            )}
                            {bill.notes && (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontWeight: 400 }}>{bill.notes}</div>
                            )}
                          </td>
                          <td className="tracker-td col-amount tracker-mono">
                            ${parseFloat(bill.amount).toFixed(2)}
                          </td>
                          <td className="tracker-td col-due">
                            {new Date(bill.due_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                          </td>
                          <td className="tracker-td col-category">
                            {bill.category
                              ? <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{bill.category}</span>
                              : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
                          </td>
                          <td className="tracker-td col-cycle">
                            {bill.is_recurring
                              ? <span className="badge badge-primary">{CYCLE_LABELS[bill.recurring_cycle] || bill.recurring_cycle}</span>
                              : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>One-time</span>}
                          </td>
                          <td className="tracker-td col-assigned">
                            {bill.assigned_to_name
                              ? <span className="badge badge-secondary">{bill.assigned_to_name}</span>
                              : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
                          </td>
                          <td className="tracker-td col-status">
                            <DaysChip dueDateStr={bill.due_date} isPaid={bill.is_paid} isRecurring={bill.is_recurring} />
                          </td>
                          <td className="tracker-td col-actions">
                            <div className="list-item-actions">
                              {!!bill.is_recurring && (
                                <button
                                  onClick={() => handleViewHistory(bill)}
                                  className="btn btn-secondary btn-sm"
                                  title="View payment history"
                                >
                                  History
                                </button>
                              )}
                              <button onClick={() => handleEdit(bill)} className="btn btn-secondary btn-sm">Edit</button>
                              <button onClick={() => handleDelete(bill.id)} className="btn btn-danger btn-sm">Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Payment History Modal */}
      {historyBill && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setHistoryBill(null)}
        >
          <div
            style={{
              background: 'var(--bg-primary)', borderRadius: '12px', padding: '28px',
              minWidth: '340px', maxWidth: '520px', width: '90%', maxHeight: '80vh',
              overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0 }}>{historyBill.name}</h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
                  Payment History · {CYCLE_LABELS[historyBill.recurring_cycle]}
                </p>
              </div>
              <button onClick={() => setHistoryBill(null)} className="btn btn-secondary btn-sm">✕</button>
            </div>

            {historyLoading ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : historyPayments.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>
                No payment history yet. Pay a cycle to start tracking.
              </p>
            ) : (
              <table className="tracker-table">
                <thead>
                  <tr>
                    <th className="tracker-th">Date Paid</th>
                    <th className="tracker-th">Period</th>
                    <th className="tracker-th">Amount</th>
                    <th className="tracker-th">Paid By</th>
                  </tr>
                </thead>
                <tbody>
                  {historyPayments.map(p => (
                    <tr key={p.id} className="tracker-row">
                      <td className="tracker-td">
                        {new Date(p.paid_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                      </td>
                      <td className="tracker-td">
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '13px' }}>
                          {p.cycle_period}
                        </span>
                      </td>
                      <td className="tracker-td tracker-mono">
                        ${parseFloat(p.amount).toFixed(2)}
                      </td>
                      <td className="tracker-td">
                        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {p.paid_by_name || p.paid_by_username}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
